import { answerRefReach } from './answer-ref.js';
/**
 * Can one student answer BOTH of these?
 *
 * Exported because the add-member picker asks it per candidate, where the faults below ask it
 * across a finished set.
 */
export function aliasMembersExclusive(a, b, tasks) {
    /* Two members in ONE task are never exclusive and need no traversal to say so — the task is
       reached as a unit, so both of its blocks are answered or neither is. */
    if (a.taskId === b.taskId)
        return false;
    return answerRefReach(a.taskId, b.taskId, tasks) === 'never'
        && answerRefReach(b.taskId, a.taskId, tasks) === 'never';
}
/**
 * Everything wrong with an alias as a SET, or an empty list.
 *
 * Empty for every single-member alias, which is the original long-name use and is untouched by
 * any of this — the rules exist for the unification case and apply only where there is
 * something to unify.
 */
export function aliasFormationFaults(alias, tasks) {
    const faults = [];
    if (alias.members.length < 2)
        return faults;
    /* ONE pair reported, not every pair. Four members that are all on the spine produce six
       complaints about one mistake, and a list a teacher scrolls is a list they stop reading. */
    outer: for (let i = 0; i < alias.members.length; i++) {
        for (let j = i + 1; j < alias.members.length; j++) {
            const a = alias.members[i];
            const b = alias.members[j];
            if (!aliasMembersExclusive(a, b, tasks)) {
                faults.push({ kind: 'notExclusive', a, b });
                break outer;
            }
        }
    }
    /*
     * TYPE, and then FACET within it. Numerical and Slider both answer with a number and
     * `aliasKind` calls them both `number`, which is why that check cannot stand in for this
     * one. And one type is not enough either: Numerical answers with a `value` and a `unit`, so
     * two numerical members can be the same type and different questions.
     *
     * A member whose block is GONE contributes neither, rather than a mismatch — that is
     * `aliasStatus`'s broken state to report, and saying it twice at two precisions is how an
     * issue list stops being read.
     */
    const types = new Set();
    const facets = new Set();
    for (const m of alias.members) {
        const block = tasks.find(t => t.id === m.taskId)?.blocks?.find(b => b.id === m.blockId);
        if (!block)
            continue;
        types.add(block.type);
        facets.add(m.facet);
    }
    if (types.size > 1)
        faults.push({ kind: 'typeMismatch', types: [...types].sort() });
    else if (facets.size > 1)
        faults.push({ kind: 'facetMismatch', facets: [...facets].sort() });
    return faults;
}
/**
 * WHICH ROUTES ARRIVE HERE WITH NOTHING — the "finish this" half.
 *
 * `aliasStatus` says whether the cover is total; this says what is missing from it, because
 * "incomplete" is not actionable and "students on Track B arrive with no answer" is. Returns
 * the tasks a student can pass through on a route that reaches `siteTaskId` without passing
 * through any member.
 *
 * Named by TASK rather than by pathway, because a pathway is a set of tasks and the teacher
 * has to go to one of them to add the block. The caller turns a task into whatever name it
 * shows — the pathway's, where it has one.
 */
export function aliasUncoveredFrom(alias, siteTaskId, tasks) {
    const members = new Set(alias.members.map(m => m.taskId));
    /*
     * A task is a gap when it can precede the site and holds no member — restricted to tasks
     * that are NOT exclusive with every member, because a task on a route no member touches is
     * precisely one a student could take instead of them.
     */
    return tasks
        .filter(t => !members.has(t.id))
        .filter(t => answerRefReach(t.id, siteTaskId, tasks) !== 'never')
        .filter(t => [...members].every(m => answerRefReach(m, t.id, tasks) === 'never'
        && answerRefReach(t.id, m, tasks) === 'never'))
        .map(t => t.id);
}
//# sourceMappingURL=alias-formation.js.map